import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import './adminViden.css';

// Hjælpefunktion til at normalisere whitespace i HTML-indhold
const normalizeWhitespace = (html) => {
  if (!html) return '';

  // Erstat alle newlines med space
  let normalized = html.replace(/\n/g, ' ');

  // Erstat multiple spaces med et enkelt space
  normalized = normalized.replace(/\s+/g, ' ');

  // Opdeler HTML i dele baseret på tags
  const parts = [];
  let currentIndex = 0;
  const regex = /<[^>]+>/g;
  let match;

  while ((match = regex.exec(normalized)) !== null) {
    // Tekst før tag
    if (match.index > currentIndex) {
      const textBefore = normalized.substring(currentIndex, match.index);
      parts.push(textBefore);
    }

    // Tag'et selv
    parts.push(match[0]);
    currentIndex = match.index + match[0].length;
  }

  // Tekst efter sidste tag
  if (currentIndex < normalized.length) {
    parts.push(normalized.substring(currentIndex));
  }

  // Genopbyg HTML, mens vi sikrer korrekt whitespace
  return parts.map(part => {
    // Hvis det er et tag, returner det som det er
    if (part.startsWith('<') && part.endsWith('>')) {
      return part;
    }
    // Hvis det er tekst (ikke et tag), trim kun leading/trailing whitespace
    return part;
  }).join('');
};

const AdminViden = () => {
  const navigate = useNavigate();
  const introTextRef = useRef(null);
  const contentRef = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [videnData, setVidenData] = useState({
    heroImage: '/media/hero/MBT-boger.avif',
    introText: 'Velkommen til vores videns afsnit, her kan du kan orientere dig i vores egne bogudgivelser, boganmeldelser af gode børnebøger og fagbøger samt videns artikler om forskellige psykologfaglige emner som optager os hver især.'
  });
  
  const [artikler, setArtikler] = useState([]);
  const [tags, setTags] = useState([
    { id: 'bog', name: 'Bog' },
    { id: 'artikel', name: 'Artikel' },
    { id: 'podcast', name: 'Podcast' }
  ]);
  
  const [editingArtikel, setEditingArtikel] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  
  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  // Format states
  const [introTextFormats, setIntroTextFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [contentFormats, setContentFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load data
  useEffect(() => {
    if (user) {
      fetchVidenData();
    }
  }, [user]);

  // Initialisering af editorer
  useEffect(() => {
    if (isEditing && introTextRef.current) {
      introTextRef.current.innerHTML = videnData.introText || '';
    }

    if (editingArtikel && contentRef.current) {
      contentRef.current.innerHTML = editingArtikel.content || '';
    }
  }, [isEditing, editingArtikel, videnData.introText]);

  // Tilføj event listener for selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      // Find ud af hvilken editor der har fokus
      const activeElement = document.activeElement;
      const isIntroText = introTextRef.current && introTextRef.current.contains(activeElement);
      const isContent = contentRef.current && contentRef.current.contains(activeElement);
      
      if (isIntroText) {
        checkActiveFormats('introText');
      } else if (isContent) {
        checkActiveFormats('content');
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isEditing, editingArtikel]);

  const handleEditorFocus = (editorType) => {
    const editor = {
      introText: introTextRef.current,
      content: contentRef.current
    }[editorType];

    if (editor) {
      editor.focus();
      checkActiveFormats(editorType);
    }
  };

  const checkActiveFormats = (editorType) => {
    const setFormats = {
      introText: setIntroTextFormats,
      content: setContentFormats
    }[editorType];
    
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false,
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink'),
      underline: document.queryCommandState('underline')
    });
  };

  // Håndter link-indsættelse
  const handleLinkInsertion = (editor) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    // Tjek om selektionen er inden for editoren
    if (!editor || !editor.contains(range.commonAncestorContainer)) {
      return;
    }
    
    // Tjek om den valgte tekst er en URL
    const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
    
    if (url && url.trim() !== '') {
      const linkText = selectedText || prompt('Indtast link tekst:', '');
      
      if (linkText && linkText.trim() !== '') {
        // Indsæt link direkte i HTML
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.textContent = linkText;
        
        // Indsæt linket
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Flyt cursoren efter linket
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleFormatClick = (formatType, editorType) => {
    const editor = {
      introText: introTextRef.current,
      content: contentRef.current
    }[editorType];

    if (!editor) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!editor.contains(document.activeElement)) {
      editor.focus();
    }

    // Udfør formateringen direkte
    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        if (!range) return;
        
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement && parentElement.tagName === 'H2';
        
        if (isHeading) {
          document.execCommand('formatBlock', false, 'p');
        } else {
          document.execCommand('formatBlock', false, 'h2');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        handleLinkInsertion(editor);
        break;
      
      case 'underline':
        document.execCommand('underline', false);
        break;
      
      default:
        break;
    }

    // Opdater aktive formateringer
    checkActiveFormats(editorType);
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    // For rich text editors
    if (e.key === 'Enter' && e.target.contentEditable === 'true') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
      return;
    }
    
    // For textarea editors
    if (e.key === 'Enter' && !e.shiftKey && e.target.tagName === 'TEXTAREA') {
      e.preventDefault();
      
      // Indsæt et linjeskift på cursor-positionen
      const { name, selectionStart, selectionEnd, value } = e.target;
      const newValue = value.substring(0, selectionStart) + "\n" + value.substring(selectionEnd);
      
      // Opdater tekstens værdi
      setEditingArtikel(prev => ({
        ...prev,
        [name]: newValue
      }));
      
      // Efter komponentopdatering, flyt cursoren til efter den nye linje
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = selectionStart + 1;
      }, 0);
    }
  };

  const fetchVidenData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch videnData
      const videnDocRef = doc(db, 'content', 'viden');
      const videnDoc = await getDoc(videnDocRef);
      
      if (videnDoc.exists()) {
        const data = videnDoc.data();
        setVidenData({
          heroImage: data.heroImage || videnData.heroImage,
          introText: data.introText || videnData.introText
        });
        
        if (data.tags) {
          setTags(data.tags);
        }
        
        if (data.artikler) {
          setArtikler(data.artikler);
        }
      }
    } catch (error) {
      console.error('Fejl ved hentning af viden data:', error);
      setMessage({
        type: 'error',
        text: 'Der opstod en fejl ved indlæsning af data.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVidenData(prev => ({ ...prev, [name]: value }));
  };

  const handleArtikelInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'title' && !editingArtikel.isExternalLink) {
      // Automatisk generering af URL baseret på titlen
      const slugifiedTitle = value.toLowerCase()
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'o')
        .replace(/å/g, 'a')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      
      setEditingArtikel(prev => ({ 
        ...prev, 
        [name]: value,
        link: `/viden/${slugifiedTitle}`
      }));
    } else {
      setEditingArtikel(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleHeroFileChange = (e) => {
    if (e.target.files[0]) {
      setHeroFile(e.target.files[0]);
      setHeroPreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setPreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(t => t !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  const handleEditArtikel = (artikel) => {
    setEditingArtikel(artikel);
    setSelectedTags(artikel.tags || []);
    setPreview(artikel.image);
    setIsCreating(false);
  };

  const handleCreateArtikel = () => {
    const newId = `artikel-${Date.now()}`;
    setEditingArtikel({
      id: newId,
      title: '',
      description: '',
      image: '',
      tags: [],
      link: `/viden/${newId}`,
      isExternalLink: false,
      content: ''
    });
    setSelectedTags([]);
    setPreview(null);
    setSelectedFile(null);
    setIsCreating(true);
  };

  const handleDeleteArtikel = async (artikelId) => {
    if (!window.confirm('Er du sikker på, at du vil slette denne artikel?')) {
      return;
    }

    try {
      setIsSaving(true);

      const artikelToDelete = artikler.find(a => a.id === artikelId);
      if (artikelToDelete && artikelToDelete.image && artikelToDelete.image.includes('firebase')) {
        // Delete image from storage if it's a firebase URL
        const imageRef = ref(storage, `viden/${artikelId}/image`);
        await deleteObject(imageRef);
      }

      // Update Firestore document
      const updatedArtikler = artikler.filter(a => a.id !== artikelId);
      const videnDocRef = doc(db, 'content', 'viden');
      await setDoc(videnDocRef, { 
        artikler: updatedArtikler,
        heroImage: videnData.heroImage,
        introText: videnData.introText,
        tags: tags
      }, { merge: true });

      setArtikler(updatedArtikler);
      setMessage({ type: 'success', text: 'Artikel er blevet slettet!' });
    } catch (error) {
      console.error('Fejl ved sletning af artikel:', error);
      setMessage({ type: 'error', text: 'Der opstod en fejl ved sletning af artikel.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLinkTypeChange = (isExternal) => {
    setEditingArtikel(prev => {
      // Hvis vi skifter fra ekstern til intern, generer automatisk link
      let updatedLink = prev.link;
      
      if (!isExternal) {
        const slugifiedTitle = prev.title.toLowerCase()
          .replace(/æ/g, 'ae')
          .replace(/ø/g, 'o')
          .replace(/å/g, 'a')
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-');
        
        updatedLink = `/viden/${slugifiedTitle || prev.id}`;
      }
      
      return {
        ...prev,
        isExternalLink: isExternal,
        link: updatedLink
      };
    });
  };

  const handleSaveArtikel = async () => {
    if (!editingArtikel.title || !editingArtikel.description) {
      setMessage({ type: 'error', text: 'Titel og beskrivelse er påkrævet.' });
      return;
    }

    if (!editingArtikel.isExternalLink && !editingArtikel.link.startsWith('/viden/')) {
      setMessage({ type: 'error', text: 'Interne links skal starte med /viden/' });
      return;
    }

    if (editingArtikel.isExternalLink && 
        (!editingArtikel.link || 
         !(editingArtikel.link.startsWith('http://') || editingArtikel.link.startsWith('https://')))) {
      setMessage({ type: 'error', text: 'Eksterne links skal starte med http:// eller https://' });
      return;
    }

    try {
      setIsSaving(true);
      let imageUrl = editingArtikel.image;

      // Upload image if a new file is selected
      if (selectedFile) {
        const imageRef = ref(storage, `viden/${editingArtikel.id}/image`);
        await uploadBytes(imageRef, selectedFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Hent det formaterede indhold fra editoren og normaliser whitespace
      const content = contentRef.current ? normalizeWhitespace(contentRef.current.innerHTML) : editingArtikel.content || '';

      const updatedArtikel = {
        ...editingArtikel,
        image: imageUrl,
        tags: selectedTags,
        content: content
      };

      let updatedArtikler;
      if (isCreating) {
        updatedArtikler = [...artikler, updatedArtikel];
      } else {
        updatedArtikler = artikler.map(a => 
          a.id === editingArtikel.id ? updatedArtikel : a
        );
      }

      // Update Firestore document
      const videnDocRef = doc(db, 'content', 'viden');
      await setDoc(videnDocRef, { 
        artikler: updatedArtikler,
        heroImage: videnData.heroImage,
        introText: videnData.introText,
        tags: tags
      }, { merge: true });

      setArtikler(updatedArtikler);
      setEditingArtikel(null);
      setSelectedFile(null);
      setPreview(null);
      setSelectedTags([]);
      setIsCreating(false);
      setMessage({ 
        type: 'success', 
        text: isCreating ? 'Ny artikel er oprettet!' : 'Artikel er opdateret!' 
      });
    } catch (error) {
      console.error('Fejl ved gem af artikel:', error);
      setMessage({ type: 'error', text: 'Der opstod en fejl ved gem af artikel.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveHovedindhold = async () => {
    try {
      setIsSaving(true);
      let heroImageUrl = videnData.heroImage;

      // Upload hero image if a new file is selected
      if (heroFile) {
        const imageRef = ref(storage, 'viden/hero');
        await uploadBytes(imageRef, heroFile);
        heroImageUrl = await getDownloadURL(imageRef);
      }

      // Hent det formaterede indhold fra editoren og normaliser whitespace
      const introText = introTextRef.current ? normalizeWhitespace(introTextRef.current.innerHTML) : videnData.introText || '';

      // Update Firestore document
      const videnDocRef = doc(db, 'content', 'viden');
      await setDoc(videnDocRef, { 
        heroImage: heroImageUrl,
        introText: introText,
        tags: tags,
        artikler: artikler
      }, { merge: true });

      setVidenData(prev => ({
        ...prev,
        heroImage: heroImageUrl,
        introText: introText
      }));
      
      setHeroFile(null);
      setHeroPreview(null);
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Hovedindhold er opdateret!' });
    } catch (error) {
      console.error('Fejl ved gem af hovedindhold:', error);
      setMessage({ type: 'error', text: 'Der opstod en fejl ved gem af hovedindhold.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreating || editingArtikel) {
      setEditingArtikel(null);
      setSelectedFile(null);
      setPreview(null);
      setSelectedTags([]);
      setIsCreating(false);
    } else {
      setIsEditing(false);
      setHeroFile(null);
      setHeroPreview(null);
    }
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user?.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-viden-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til Admin
          </button>
          
          <h1>Administrer Viden</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          {/* Hovedindhold sektion */}
          {!editingArtikel && !isCreating && (
            <div className="admin-section">
              <div className="admin-section-header">
                <h2>Hovedindhold</h2>
                {!isEditing ? (
                  <button 
                    className="edit-content-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    Rediger hovedindhold
                  </button>
                ) : null}
              </div>
              
              {!isEditing ? (
                <div className="preview-section">
                  <div className="preview-hero-image">
                    <img src={videnData.heroImage} alt="Hero billede" />
                  </div>
                  <div className="admin-description">
                    <div dangerouslySetInnerHTML={{ __html: videnData.introText }}></div>
                  </div>
                </div>
              ) : (
                <div className="viden-form">
                  <div className="form-group">
                    <label>Hero billede</label>
                    <div className="image-uploader">
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleHeroFileChange} 
                        id="hero-image"
                      />
                      <label htmlFor="hero-image" className="upload-btn">
                        Vælg billede
                      </label>
                    </div>
                    <div className="hero-preview">
                      {(heroPreview || videnData.heroImage) && (
                        <img 
                          src={heroPreview || videnData.heroImage} 
                          alt="Hero billede forhåndsvisning" 
                        />
                      )}
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Introduktionstekst</label>
                    <FormatBar 
                      activeFormats={introTextFormats}
                      onFormatClick={(format) => handleFormatClick(format, 'introText')}
                    />
                    <div
                      ref={introTextRef}
                      className="rich-text-editor"
                      contentEditable
                      onKeyDown={handleKeyDown}
                      onFocus={() => handleEditorFocus('introText')}
                      data-placeholder="Skriv introduktionstekst her..."
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button 
                      className="save-btn"
                      onClick={handleSaveHovedindhold}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Gemmer...' : 'Gem'}
                    </button>
                    <button 
                      className="cancel-btn"
                      onClick={handleCancel}
                      disabled={isSaving}
                    >
                      Annuller
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Artikler sektion */}
          {!editingArtikel && !isCreating && !isEditing ? (
            <div className="admin-section">
              <div className="admin-section-header">
                <h2>Videnartikler</h2>
                <button 
                  className="create-btn"
                  onClick={handleCreateArtikel}
                >
                  + Opret ny artikel
                </button>
              </div>
              
              {artikler.length === 0 ? (
                <div className="no-items">
                  Der er ingen videnartikler oprettet endnu. Klik på "Opret ny artikel" for at komme i gang.
                </div>
              ) : (
                <div className="artikel-list">
                  {artikler.map((artikel) => (
                    <div key={artikel.id} className="artikel-item">
                      <div className="artikel-preview">
                        <div className="artikel-image">
                          <img src={artikel.image} alt={artikel.title} />
                        </div>
                        <div className="artikel-content">
                          <h3>{artikel.title}</h3>
                          <p>{artikel.description}</p>
                          <div className="artikel-tags">
                            {artikel.tags && artikel.tags.map(tagId => (
                              <span key={tagId} className="artikel-tag">
                                {tags.find(t => t.id === tagId)?.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="artikel-actions">
                        <button
                          className="edit-btn"
                          onClick={() => handleEditArtikel(artikel)}
                        >
                          Rediger
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteArtikel(artikel.id)}
                        >
                          Slet
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
          
          {/* Artikel formular */}
          {(editingArtikel || isCreating) && (
            <div className="admin-section">
              <h2>{isCreating ? 'Opret ny artikel' : 'Rediger artikel'}</h2>
              <div className="artikel-form">
                <div className="form-group">
                  <label>Titel</label>
                  <input
                    type="text"
                    name="title"
                    value={editingArtikel.title}
                    onChange={handleArtikelInputChange}
                    placeholder="Titel på artikel"
                  />
                </div>
                
                <div className="form-group">
                  <label>Beskrivelse</label>
                  <textarea
                    name="description"
                    value={editingArtikel.description}
                    onChange={handleArtikelInputChange}
                    rows={4}
                    placeholder="Kort beskrivelse af artikel"
                    onKeyDown={handleKeyDown}
                  />
                </div>
                
                <div className="form-group">
                  <label>Type af artikel</label>
                  <div className="link-type-selector">
                    <div className="radio-option">
                      <input
                        type="radio"
                        id="internal-link"
                        name="link-type"
                        checked={!editingArtikel.isExternalLink}
                        onChange={() => handleLinkTypeChange(false)}
                      />
                      <label htmlFor="internal-link">Intern artikel med indhold</label>
                    </div>
                    <div className="radio-option">
                      <input
                        type="radio"
                        id="external-link"
                        name="link-type"
                        checked={editingArtikel.isExternalLink}
                        onChange={() => handleLinkTypeChange(true)}
                      />
                      <label htmlFor="external-link">Eksternt link</label>
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>{editingArtikel.isExternalLink ? 'Eksternt link' : 'URL'}</label>
                  <input
                    type="text"
                    name="link"
                    value={editingArtikel.link}
                    onChange={handleArtikelInputChange}
                    placeholder={editingArtikel.isExternalLink ? 'https://eksempel.dk/...' : '/viden/artikel-url'}
                  />
                  <p className="form-help-text">
                    {editingArtikel.isExternalLink 
                      ? 'Indtast det komplette URL, inklusive https://' 
                      : 'URL genereres automatisk baseret på titel, men kan redigeres manuelt'}
                  </p>
                </div>
                
                {!editingArtikel.isExternalLink && (
                  <div className="form-group">
                    <label>Artikelindhold</label>
                    <FormatBar 
                      activeFormats={contentFormats}
                      onFormatClick={(format) => handleFormatClick(format, 'content')}
                    />
                    <div
                      ref={contentRef}
                      className="rich-text-editor"
                      contentEditable
                      onKeyDown={handleKeyDown}
                      onFocus={() => handleEditorFocus('content')}
                      data-placeholder="Skriv dit artikelindhold her..."
                    />
                    <p className="form-help-text">
                      Du kan formatere din tekst ved hjælp af formateringsværktøjerne ovenfor
                    </p>
                  </div>
                )}
                
                <div className="form-group">
                  <label>Billede</label>
                  <div className="image-uploader">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileChange} 
                      id="artikel-image"
                    />
                    <label htmlFor="artikel-image" className="upload-btn">
                      Vælg billede
                    </label>
                  </div>
                  <div className="image-preview">
                    {(preview || editingArtikel.image) && (
                      <img 
                        src={preview || editingArtikel.image} 
                        alt="Billedforhåndsvisning" 
                      />
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Tags</label>
                  <div className="tag-selector">
                    {tags.map(tag => (
                      <div key={tag.id} className="tag-option">
                        <input
                          type="checkbox"
                          id={`tag-${tag.id}`}
                          checked={selectedTags.includes(tag.id)}
                          onChange={() => handleTagToggle(tag.id)}
                        />
                        <label htmlFor={`tag-${tag.id}`}>{tag.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSaveArtikel}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Gemmer...' : 'Gem'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminViden;
